/**
 * Resource mapping based on displayName
 * Maps user-friendly names to resource identifiers
 */

export const RESOURCE_DISPLAY_NAMES = {
  OVERVIEW: 'Overview',
  CREATE_JOB_OPENING: 'Create Job Opening',
  VIEW_JOB_OPENING: 'View Job Openings',
  SCHEDULE_INTERVIEW: 'Schedule Interview',
  VIEW_INTERVIEWS: 'View Interviews ',
  PROCTORING_ANALYSIS: 'Proctoring Analysis',
  USER_MANAGEMENT: 'User Management',
  SYSTEM_SETTINGS: 'Settings',
};

export const ACTION_NAMES = {
  GET_DATA: 'Get Data',
  ADD_NEW: 'Add New',
  EDIT_ITEM: 'Edit Item',
  DELETE_BUTTON: 'Delete Button',
  VIEW: 'View',
  UPDATE: 'Update',
  DELETE: 'Delete',
  ASSIGN: 'Assign',
  SCHEDULE: 'Schedule',
};

const normalize = (value) => String(value || '').trim().toLowerCase();

const resourceNames = (permission) => [
  permission?.resource?.displayName,
  permission?.resource?.resourceKey,
  permission?.resource?.name,
  permission?.resource_display_name,
  permission?.resource_name,
].filter(Boolean);

const actionNames = (permission) => [
  permission?.action?.name,
  permission?.action_name,
  permission?.actionName,
].filter(Boolean);

/**
 * Get resource by displayName from permissions array
 * @param {Array} permissions - User's permissions
 * @param {string} displayName - Display name to search for
 * @returns {Object|null} - Resource object if found
 */
export const getResourceByDisplayName = (permissions, displayName) => {
  if (!permissions || permissions.length === 0) return null;
  
  const perm = permissions.find(p => resourceNames(p).some((name) => normalize(name) === normalize(displayName)));
  return perm ? perm.resource : null;
};

/**
 * Get all actions for a specific displayName
 * @param {Array} permissions - User's permissions
 * @param {string} displayName - Display name to search for
 * @returns {Array} - Array of action names
 */
export const getActionsForResource = (permissions, displayName) => {
  if (!permissions || permissions.length === 0) return [];
  
  return permissions
    .filter(p => resourceNames(p).some((name) => normalize(name) === normalize(displayName)))
    .map(p => p.action?.name)
    .filter(Boolean);
};

/**
 * Check if user has specific action for a resource by displayName
 * @param {Array} permissions - User's permissions
 * @param {string} displayName - Resource displayName
 * @param {string} actionName - Action name (e.g., 'Get Data', 'Add New')
 * @returns {boolean}
 */
export const hasActionForResource = (permissions, displayName, actionName) => {
  if (!permissions || permissions.length === 0) return false;

  const normalizedDisplayName = normalize(displayName);
  const normalizedActionName = normalize(actionName);

  return permissions.some((p) => {
    const matchesResource = resourceNames(p).some((name) => normalize(name) === normalizedDisplayName);
    if (!matchesResource) return false;

    const actionMatches = actionNames(p).some((name) => normalize(name) === normalizedActionName);
    if (actionMatches) return true;

    const actionLabel = normalize(p?.action?.name || p?.action_name || p?.actionName || '');
    const aliasMap = {
      'get data': 'view',
      'add new': 'schedule',
      'edit item': 'update',
      'delete button': 'delete',
      'view': 'view',
      'schedule': 'schedule',
      'update': 'update',
      'delete': 'delete',
      'assign': 'assign',
    };

    return aliasMap[actionLabel] === normalizedActionName;
  });
};

/**
 * Check if user has any access to a resource by displayName
 * @param {Array} permissions - User's permissions
 * @param {string} displayName - Resource displayName
 * @returns {boolean}
 */
export const hasResourceAccess = (permissions, displayName) => {
  if (!permissions || permissions.length === 0) return false;
  
  return permissions.some(p => resourceNames(p).some((name) => normalize(name) === normalize(displayName)));
};

/**
 * Get permission details for a resource and action
 * @param {Array} permissions - User's permissions
 * @param {string} displayName - Resource displayName
 * @param {string} actionName - Action name
 * @returns {Object|null} - Full permission object if found
 */
export const getPermissionDetails = (permissions, displayName, actionName) => {
  if (!permissions || permissions.length === 0) return null;

  const normalizedDisplayName = normalize(displayName);
  const normalizedActionName = normalize(actionName);

  return permissions.find((p) => {
    const matchesResource = resourceNames(p).some((name) => normalize(name) === normalizedDisplayName);
    if (!matchesResource) return false;

    const actionMatches = actionNames(p).some((name) => normalize(name) === normalizedActionName);
    if (actionMatches) return true;

    const actionLabel = normalize(p?.action?.name || p?.action_name || p?.actionName || '');
    const aliasMap = {
      'get data': 'view',
      'add new': 'schedule',
      'edit item': 'update',
      'delete button': 'delete',
      'view': 'view',
      'schedule': 'schedule',
      'update': 'update',
      'delete': 'delete',
      'assign': 'assign',
    };

    return aliasMap[actionLabel] === normalizedActionName;
  }) || null;
};
